import { intlFormat } from 'date-fns';
import { CalendarIcon, ClockIcon, PinIcon, UserIcon } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { CustomerIcon } from '../../../assets/icons/customer-icon.tsx';
import { AutoSaveIndicator } from '../../../components/common/auto-save-indicator.tsx';
import { LoadingBlockFullPage } from '../../../components/loading/loading-block-full-page.tsx';
import { DrawerContainer, DrawerContent, DrawerHeader } from '../../../components/ui/drawer.tsx';
import { LinkText } from '../../../components/ui/link-text.tsx';
import { Text } from '../../../components/ui/text.tsx';
import { useFormAutoSave } from '../../../lib/hooks/use-form-auto-save.ts';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { CrmNoteInput } from '../../../server/dto/crm-note-input-dto.ts';
import type { RouterOutput } from '../../../server/types/infer-trpc-types.ts';
import { api } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { CommentsThread } from '../../comments/components/comments-thread.tsx';
import { DialogForm } from '../../dialog/dialog-form.tsx';
import { Badge } from '../../list-views/components/badge.tsx';
import { useUpdateNote } from '../hooks/use-update-note';
import { getNoteTypeLabel } from '../utils/get-note-type-label';
import { NoteFormFields } from './note-form-fields';
import { NoteOptionsDropdown } from './note-options-dropdown';

type NoteDetailsFormProps = {
    note: RouterOutput['platoUser']['query']['notes']['getNoteById'];
    className?: string;
    autoSaveIndicator?: React.ReactNode;
    onBack?: () => void;
    onClose?: () => void;
    onUpdate?: (values: CrmNoteInput) => void;
};

const NoteDetailsForm = ({
    note: noteDetails,
    className,
    onBack,
    onClose,
    onUpdate,
    autoSaveIndicator,
}: NoteDetailsFormProps) => {
    const { t, locale } = useLocale('notes');
    const sendEvent = useAnalytics();
    const navigate = useNavigate();

    const { control, reset, watch, formState } = useForm<CrmNoteInput>({
        mode: 'onChange',
        defaultValues: {
            title: noteDetails.titleRich,
        },
    });

    useEffect(() => {
        sendEvent('customer_interaction_viewed', {
            note_id: noteDetails.noteId,
            customer_id: noteDetails.customerId ?? 'unknown',
            note_type: noteDetails.noteType,
        });
    }, [sendEvent, noteDetails.noteId, noteDetails.customerId, noteDetails.noteType]);

    // Update form with data when it's loaded
    useEffect(() => {
        reset(
            {
                title: noteDetails.titleRich,
            },
            {
                keepValues: true,
            },
        );
    }, [noteDetails, reset]);

    // Important that we read state here, otherwise the variable will not be updated
    const isFormValid = formState.isValid;

    const handleAutoSave = (values: CrmNoteInput) => {
        if (noteDetails && isFormValid) {
            onUpdate?.(values as CrmNoteInput);
        }
    };

    const autoSaveHandlerRef = useRef(handleAutoSave);
    autoSaveHandlerRef.current = handleAutoSave;

    // Auto-save when form values change and the form is dirty
    useFormAutoSave(
        watch,
        (values) => {
            autoSaveHandlerRef.current(values);
        },
        1000,
    );

    return (
        <DrawerContainer className={className}>
            <DrawerHeader
                onBack={onBack}
                onClose={onClose}
                actions={<NoteOptionsDropdown note={noteDetails} onDelete={onBack ?? onClose} />}
                title={getNoteTypeLabel(noteDetails.noteType, t)}
            >
                <div className="flex flex-row items-center gap-2">
                    {noteDetails.pinnedAt && (
                        <Badge icon={PinIcon} color="yellow">
                            {t('notes__pinned_label', { ns: 'notes' })}
                        </Badge>
                    )}
                    {autoSaveIndicator}
                </div>
            </DrawerHeader>
            <DrawerContent>
                {noteDetails && (
                    <NoteFormFields
                        noteType={noteDetails.noteType}
                        noteId={noteDetails.noteId}
                        control={control}
                        errors={formState.errors}
                    />
                )}

                {noteDetails && (
                    <DialogForm.ItemList className="rounded-lg bg-white py-2 px-3 shadow-xs" grid>
                        {/* Customer Field */}
                        <DialogForm.Item className="items-start">
                            <DialogForm.Label icon={CustomerIcon}>
                                {t('notes__form__customer_label', { ns: 'notes' })}
                            </DialogForm.Label>
                            <DialogForm.Body className="flex flex-col gap-1 py-1.5 ">
                                {noteDetails.customerId && noteDetails.customer ? (
                                    <LinkText
                                        href={`/customers/${noteDetails.customerId}`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            navigate(`/customers/${noteDetails.customerId}`);
                                        }}
                                    >
                                        {noteDetails.customer.customerName}
                                    </LinkText>
                                ) : (
                                    <Text color="neutral-500">No customer assigned</Text>
                                )}
                            </DialogForm.Body>
                        </DialogForm.Item>

                        {/* Date and time fields - only for VISIT, EVENT and PHONE_CALL */}
                        {noteDetails.noteType !== 'NOTE' && (
                            <>
                                <DialogForm.Item className="items-start">
                                    <DialogForm.Label icon={CalendarIcon}>
                                        {t('notes__form__date_label', { ns: 'notes' })}
                                    </DialogForm.Label>
                                    <DialogForm.Body className="py-1.5">
                                        <Text color="neutral-950">
                                            {intlFormat(
                                                noteDetails.occurredAt,
                                                {
                                                    month: 'long',
                                                    day: 'numeric',
                                                    year: 'numeric',
                                                },
                                                {
                                                    locale,
                                                },
                                            )}
                                        </Text>
                                    </DialogForm.Body>
                                </DialogForm.Item>
                                <DialogForm.Item className="items-start">
                                    <DialogForm.Label icon={ClockIcon}>
                                        {t('notes__form__time_label', { ns: 'notes' })}
                                    </DialogForm.Label>
                                    <DialogForm.Body className="py-1.5">
                                        <Text color="neutral-950">
                                            {intlFormat(
                                                noteDetails.occurredAt,
                                                {
                                                    hour: 'numeric',
                                                    minute: 'numeric',
                                                },
                                                {
                                                    locale,
                                                },
                                            )}
                                        </Text>
                                    </DialogForm.Body>
                                </DialogForm.Item>
                            </>
                        )}

                        {/* Created By Field */}
                        <DialogForm.Item>
                            <DialogForm.Label icon={UserIcon}>
                                {t('notes__form__created_by_label', { ns: 'notes' })}
                            </DialogForm.Label>
                            <DialogForm.Body className="items-center">
                                <Text color="neutral-600">
                                    {noteDetails.createdBy.firstName} {noteDetails.createdBy.lastName}
                                </Text>
                            </DialogForm.Body>
                        </DialogForm.Item>
                    </DialogForm.ItemList>
                )}

                <CommentsThread noteId={noteDetails.noteId} />
            </DrawerContent>
        </DrawerContainer>
    );
};

export type NoteDetailsProps = Omit<NoteDetailsFormProps, 'note' | 'onUpdate' | 'autoSaveIndicator'> & {
    noteId: string;
};

export const NoteDetails = ({ noteId, ...props }: NoteDetailsProps) => {
    const { data: noteDetails, isLoading } = api.platoUser.query.notes.getNoteById.useQuery(
        {
            noteId,
        },
        {
            enabled: !!noteId,
        },
    );

    const updateNoteMutation = useUpdateNote();

    if (isLoading || !noteDetails) {
        return <LoadingBlockFullPage className="mt-64" />;
    }

    return (
        <NoteDetailsForm
            note={noteDetails}
            autoSaveIndicator={
                <AutoSaveIndicator
                    isUpdating={updateNoteMutation.isLoading}
                    isSuccess={updateNoteMutation.isSuccess}
                    isError={updateNoteMutation.isError}
                />
            }
            onUpdate={(data) => {
                updateNoteMutation.mutate({
                    ...data,
                    noteId,
                });
            }}
            {...props}
        />
    );
};
